import { useTranslation } from 'react-i18next'
import type { LucideIcon } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import { TARGETING_GROUPS, type TargetingTypeDef } from '@/services/targeting/targeting'

/** One targeting type the picker offers. */
export interface TargetingTypeOption {
  id: string
  group: TargetingTypeDef['group']
  icon: LucideIcon
  label: string
  help: string
  /** How much this type already targets; 0 for one not in use yet. */
  count?: number
}

export interface TargetingTypePickerProps {
  /** Every type, grouped and shown as cards — the ones in use included. */
  options: TargetingTypeOption[]
  /** Called with the id of the card that was picked. */
  onSelect: (id: string) => void
  /** Shown when every type has already been added. */
  emptyMessage: string
}

/**
 * The "add targeting" chooser: every targeting type as a card with its icon,
 * name and what it does. Picking one opens its editor — the types already in
 * use stay on offer, marked with what they hold, so they can be reached from
 * here as well as from the list beside it.
 */
export function TargetingTypePicker({ options, onSelect, emptyMessage }: TargetingTypePickerProps) {
  const { t } = useTranslation()

  if (!options.length) return <p className="text-sm text-muted-foreground">{emptyMessage}</p>

  return (
    <div className="flex flex-col gap-6">
      {TARGETING_GROUPS.map((group) => {
        const inGroup = options.filter((option) => option.group === group)
        if (!inGroup.length) return null
        return (
          <div key={group} className="flex flex-col gap-2">
            <div className="text-[11px] font-semibold tracking-wider text-muted-foreground uppercase">
              {t(`targeting.groups.${group}`)}
            </div>
            <div className="grid grid-cols-2 gap-2 lg:grid-cols-3 2xl:grid-cols-4">
              {inGroup.map((option) => {
                const inUse = (option.count ?? 0) > 0
                return (
                <Card
                  key={option.id}
                  size="sm"
                  role="button"
                  tabIndex={0}
                  onClick={() => onSelect(option.id)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault()
                      onSelect(option.id)
                    }
                  }}
                  className={cn(
                    'cursor-pointer gap-2 [--card-spacing:--spacing(3)] transition-colors hover:bg-accent/40 hover:ring-primary focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none',
                    // A type already targeting something stays on offer, but
                    // says so rather than looking untouched.
                    inUse && 'bg-(--ad-primary-soft)/40 ring-1 ring-primary'
                  )}
                >
                  <CardHeader>
                    <CardTitle className="text-sm">
                      {/* The icon beside the name, in a tinted tile, so the
                          types are told apart at a glance. */}
                      <span className="flex size-7 shrink-0 items-center justify-center rounded-lg bg-(--ad-primary-soft) text-(--ad-link)">
                        <option.icon className="size-4" />
                      </span>
                      <span className="min-w-0 truncate">{option.label}</span>
                      {inUse && (
                        <Badge variant="blue" className="ml-auto tabular-nums">
                          {option.count}
                        </Badge>
                      )}
                    </CardTitle>
                    <CardDescription className="line-clamp-2 text-xs">
                      {option.help}
                    </CardDescription>
                  </CardHeader>
                </Card>
                )
              })}
            </div>
          </div>
        )
      })}
    </div>
  )
}
